const clampProbability = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  if (numericValue <= 0) {
    return 0;
  }

  if (numericValue >= 1) {
    return 1;
  }

  return numericValue;
};

const toNumericArray = (values) =>
  Array.isArray(values)
    ? values
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value))
    : [];

const normalizeIsotonicCurve = (calibration) => {
  const xs = toNumericArray(calibration.xs);
  const ys = toNumericArray(calibration.ys);
  if (xs.length !== ys.length || xs.length < 2) {
    return null;
  }

  const points = xs
    .map((x, index) => ({
      x: clampProbability(x),
      y: clampProbability(ys[index]),
    }))
    .sort((left, right) => left.x - right.x);

  const deduped = points.reduce((items, point) => {
    const lastPoint = items[items.length - 1];
    if (lastPoint && Math.abs(lastPoint.x - point.x) <= 1e-9) {
      lastPoint.y = Math.max(lastPoint.y, point.y);
      return items;
    }

    items.push({ ...point });
    return items;
  }, []);

  for (let index = 1; index < deduped.length; index += 1) {
    deduped[index].y = Math.max(deduped[index].y, deduped[index - 1].y);
  }

  if (deduped.length < 2) {
    return null;
  }

  return {
    type: "isotonic",
    xs: deduped.map((point) => point.x),
    ys: deduped.map((point) => point.y),
  };
};

const normalizeBucketedCalibration = (calibration) => {
  if (!calibration || calibration.type !== "bucketed_isotonic") {
    return null;
  }

  const buckets = Array.isArray(calibration.buckets)
    ? calibration.buckets
        .map((bucket) => {
          const maxValue = Number(bucket?.max_value);
          if (!Number.isFinite(maxValue)) {
            return null;
          }

          const normalizedCurve = normalizeIsotonicCurve(
            bucket?.calibration ?? bucket
          );
          return {
            max_value: maxValue,
            label:
              typeof bucket?.label === "string" && bucket.label.trim().length > 0
                ? bucket.label
                : `<= ${maxValue}`,
            example_count: Number.isFinite(Number(bucket?.example_count))
              ? Number(bucket.example_count)
              : null,
            calibration: normalizedCurve,
          };
        })
        .filter(Boolean)
        .sort((left, right) => left.max_value - right.max_value)
    : [];

  if (buckets.length === 0) {
    return null;
  }

  const fallback = normalizeIsotonicCurve(calibration.fallback);
  return {
    type: "bucketed_isotonic",
    bucket_feature:
      typeof calibration.bucket_feature === "string"
        ? calibration.bucket_feature
        : "reviews_since_last_failure",
    buckets,
    fallback,
    source_model:
      typeof calibration.source_model === "string"
        ? calibration.source_model
        : null,
  };
};

export const normalizeIsotonicCalibration = (calibration) => {
  if (!calibration || typeof calibration !== "object") {
    return null;
  }

  if (calibration.type === "isotonic") {
    return normalizeIsotonicCurve(calibration);
  }

  if (calibration.type === "bucketed_isotonic") {
    return normalizeBucketedCalibration(calibration);
  }

  return null;
};

export const selectIsotonicCalibrationBucket = (
  calibration,
  bucketValue = null
) => {
  const normalizedCalibration = normalizeIsotonicCalibration(calibration);
  if (!normalizedCalibration) {
    return null;
  }

  if (normalizedCalibration.type === "isotonic") {
    return {
      label: "global",
      max_value: null,
      calibration: normalizedCalibration,
    };
  }

  const numericBucketValue =
    bucketValue == null ? Number.NaN : Number(bucketValue);
  const selectedBucket = Number.isFinite(numericBucketValue)
    ? normalizedCalibration.buckets.find(
        (bucket) => numericBucketValue <= bucket.max_value
      ) ?? normalizedCalibration.buckets[normalizedCalibration.buckets.length - 1]
    : null;

  if (selectedBucket?.calibration) {
    return selectedBucket;
  }

  if (normalizedCalibration.fallback) {
    return {
      label: selectedBucket?.label ?? "global",
      max_value: selectedBucket?.max_value ?? null,
      calibration: normalizedCalibration.fallback,
    };
  }

  return selectedBucket ?? null;
};

export const applyIsotonicCalibration = (
  probability,
  calibration,
  bucketValue = null
) => {
  const clampedProbability = clampProbability(probability);
  const selectedCalibration = selectIsotonicCalibrationBucket(
    calibration,
    bucketValue
  );
  if (!selectedCalibration?.calibration) {
    return clampedProbability;
  }

  const { xs, ys } = selectedCalibration.calibration;
  if (clampedProbability <= xs[0]) {
    return ys[0];
  }
  if (clampedProbability >= xs[xs.length - 1]) {
    return ys[ys.length - 1];
  }

  for (let index = 1; index < xs.length; index += 1) {
    if (clampedProbability > xs[index]) {
      continue;
    }

    const leftX = xs[index - 1];
    const rightX = xs[index];
    const leftY = ys[index - 1];
    const rightY = ys[index];
    const span = rightX - leftX;
    if (span <= 1e-9) {
      return rightY;
    }

    const position = (clampedProbability - leftX) / span;
    return leftY + position * (rightY - leftY);
  }

  return ys[ys.length - 1];
};

export const applyIsotonicCalibrationBatch = (
  probabilities,
  calibration,
  bucketValues = null
) =>
  probabilities.map((probability, index) =>
    applyIsotonicCalibration(
      probability,
      calibration,
      Array.isArray(bucketValues) ? bucketValues[index] : null
    )
  );
